import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useState, type FC, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, ICONS, LinkUnstyled, Tile, unit } from '@hcr/ui'

import { Path } from '../../../../models'
import { to } from '../../../../utils'
import {
  Background,
  BookingList,
  LayoutNavigation,
  Metadata,
  OwnerBuyAccommodationDrawer,
  WarningMissingDataFloating,
} from '../../../common'
import { ButtonBuyAccommodationCard } from '../../../common/button-buy-accommodation'
import { Coupons } from '../../../coupons'
import { BookingListEmpty } from '../booking-list-empty'
import { BookingListPreview } from '../booking-list-preview'

interface LayoutHomeProps {
  bookingOverview?: ReactNode
  content: ReactNode
  header: ReactNode
  imageUrl: string
  showFallbackWarning?: boolean
}

export const LayoutHome: FC<LayoutHomeProps> = ({
  bookingOverview,
  content,
  header,
  imageUrl,
  showFallbackWarning = false,
}) => {
  const { t } = useTranslation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const openDrawer = () => setIsDrawerOpen(true)
  const closeDrawer = () => setIsDrawerOpen(false)

  return (
    <LayoutNavigation showContextMenuButton>
      <Metadata title={t('page-title.home')} />
      {showFallbackWarning && <WarningMissingDataFloating />}
      <Box paddingX={unit(3)} bgcolor={COLORS.white}>
        <Box position='relative'>
          <Box position='absolute' sx={{ inset: 0 }}>
            <Background sx={{ height: unit(93) }} url={imageUrl} />
          </Box>
          <Stack position='relative' zIndex={1} paddingTop={unit(42)} direction='column' gap={unit(3)}>
            {bookingOverview}
            {header}
          </Stack>
        </Box>
        <Stack gap={unit(3)} paddingY={unit(3)}>
          <Coupons />
          <ButtonBuyAccommodationCard
            isDrawerOpen={isDrawerOpen}
            onClickOpenDrawer={openDrawer}
            variant='outlined'
            color='white'
          />

          {content}

          <Tile cta={null} icon={<FontAwesomeIcon icon={ICONS.falCalendarHeart} />} title={t('home.your-bookings')}>
            <BookingList empty={BookingListEmpty} preview={BookingListPreview} />
          </Tile>
          <Tile
            cta={
              <Button
                component={LinkUnstyled}
                to={to(Path.ContextMenuDestinations)}
                variant='contained'
                color='primary'
              >
                {t('home.browse-destinations')}
              </Button>
            }
            icon={<FontAwesomeIcon icon={ICONS.farSearch} />}
            title={t('home.browse-destinations')}
          >
            <Typography variant='bodyM'>{t('home.explore-and-book-services')}</Typography>
          </Tile>
        </Stack>
      </Box>

      <OwnerBuyAccommodationDrawer isOpen={isDrawerOpen} onClose={closeDrawer} />
    </LayoutNavigation>
  )
}
